import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, Lightning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useT } from '@/i18n/LanguageContext';
import { shizaiApi, yen } from './shizaiApi';
import OrderLineCard from './OrderLineCard';
import { todayJst } from '@/utils/jstDate';

const NONE = '__none__';
const NO_SUP = '__no_sup__'; // key for "仕入先未指定" lines when filtering
const newLineKey = () => Math.random().toString(36).slice(2);
const emptyLine = () => ({
  _key: newLineKey(),
  orig_idx: null,           // index in prefillRequest.lines (null = added in dialog)
  supplier_id: null,        // per-line supplier from the request (drives filter chips)
  seibang: '', unit_no: '', part_no: '', drawing_no: '', revision: '',
  name: '', material: '', maker: '', maker_pn: '', model_no: '',
  supply_type: '', qty: 0, uom: '個', unit_price: 0, item_id: '',
  // Fields carried from the source request (visible on the compact card so the
  // buyer can click to open the requester's purchase link or reference photo).
  reference_url: '', photo_url: '', was_tax_inclusive: false, request_line_notes: '',
});
const today = () => todayJst();

/**
 * Build the initial line list from a prefillRequest, preserving each line's
 * original index (`orig_idx`) + per-line supplier so we can later:
 *  (a) filter which lines belong to the picked supplier, and
 *  (b) send `request_line_indexes: [orig_idx, ...]` to the backend so
 *      ONLY those lines get marked as `ordered` on the parent request.
 */
function _linesFromRequest(r) {
  // Auto-strip 10% tax if the source request was flagged 税込み. This is the
  // "税込み flag on 発注依頼" concept — requesters paste tax-inclusive prices
  // from Amazon / MonotaRO / consumer-facing pages, so the buyer's PO must
  // strip the tax before B2B accounting picks it up. Divisor is derived from
  // the current PO tax_rate (0.10 default) — assume the request used the same
  // rate as the buyer's default which is the common Japanese consumption tax.
  const stripTax = r.is_tax_inclusive === true;
  const factor = stripTax ? (1 / 1.10) : 1;
  const reqLines = (r.lines && r.lines.length) ? r.lines : [{
    seibang: r.seibang || '', unit_no: r.unit_no || '', part_no: r.part_no || '',
    name: r.name || '', material: r.material || '', qty: r.qty || 0,
    uom: r.uom || '個', item_id: r.item_id || '',
  }];
  return reqLines.map((ln, i) => ({
    ...emptyLine(),
    orig_idx: i,
    supplier_id: ln.supplier_id || null,
    line_status: ln.line_status || 'open',
    seibang: ln.seibang || '', unit_no: ln.unit_no || '', part_no: ln.part_no || '',
    drawing_no: ln.drawing_no || '', name: ln.name || '', material: ln.material || '',
    maker: ln.maker || '', maker_pn: ln.maker_pn || '', model_no: ln.model_no || '',
    qty: ln.qty || 0, uom: ln.uom || '個', item_id: ln.item_id || '',
    unit_price: ln.unit_price ? Math.round(ln.unit_price * factor) : 0,
    reference_url: ln.reference_url || '',
    photo_url: ln.photo_url || '',
    was_tax_inclusive: stripTax && !!ln.unit_price, // chip signal for OrderLineCard
    request_line_notes: ln.notes || '',
  }));
}

export default function OrderFormDialog({ open, order, prefillRequest, onClose, onSaved }) {
  const { t } = useT();
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [orderCategories, setOrderCategories] = useState([]);
  // Auto-suggestion metadata for the 受注分類 picker (shown as a chip so buyers
  // know WHY we picked X — "部門実績 8/10" builds trust and lets them override).
  const [catSuggestion, setCatSuggestion] = useState(null); // { source, match_count, total_recent_pos, category_code }
  const [supplierId, setSupplierId] = useState('');
  const [orderCategoryId, setOrderCategoryId] = useState('');
  const [orderDate, setOrderDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [taxRate, setTaxRate] = useState(0.10);
  const [shippingCost, setShippingCost] = useState(0);
  // 単価は税込み入力 — ON のとき入力単価を税込とみなし、保存時に税抜へ換算 (÷(1+税率))
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [requestId, setRequestId] = useState(null);
  // All lines from the source request (kept intact for supplier-filter switching)
  const [reqAllLines, setReqAllLines] = useState([]);
  // Lines currently visible/editable in the dialog (subset of reqAllLines)
  const [lines, setLines] = useState([emptyLine()]);
  const [notes, setNotes] = useState('');
  const [orderType, setOrderType] = useState('purchase');
  const [busy, setBusy] = useState(false);
  // When a multi-supplier request is being ordered, the buyer picks ONE
  // supplier here and only that supplier's lines are placed into the PO.
  // `__all__` = no filter (backward-compat for single-supplier requests).
  const [supFilter, setSupFilter] = useState('__all__');
  // Per-line supplier suggestion candidates (keyed by REQ line's orig_idx, so
  // the map survives the supplier filter re-slicing lines[]). Populated once
  // when the dialog opens with a prefillRequest that has ≥1 open line without
  // a supplier. Cleared when the buyer applies a chip.
  const [suggestByOrigIdx, setSuggestByOrigIdx] = useState({});

  useEffect(() => {
    if (!open) return;
    shizaiApi.suppliers().then(setSuppliers).catch(() => {});
    shizaiApi.items().then(setItems).catch(() => {});
    shizaiApi.orderCategories().then(setOrderCategories).catch(() => {});
    if (order) {
      setSupplierId(order.supplier_id || '');
      setOrderCategoryId(order.order_category_id || '');
      setCatSuggestion(null);
      setOrderDate(order.order_date || today());
      setDueDate(order.due_date || '');
      setDeliveryLocation(order.delivery_location || '');
      setTaxRate(order.tax_rate ?? 0.10);
      setShippingCost(order.shipping_cost || 0);
      setTaxInclusive(false);
      setRequestId(order.request_id || null);
      setNotes(order.notes || '');
      setOrderType(order.order_type || 'purchase');
      setReqAllLines([]);
      setSupFilter('__all__');
      setSuggestByOrigIdx({});
      setLines((order.lines || []).map((l) => ({ ...emptyLine(), ...l, item_id: l.item_id || '' })));
    } else if (prefillRequest) {
      const r = prefillRequest;
      setOrderDate(today());
      setDueDate(r.needed_by || '');
      setDeliveryLocation('');
      setTaxRate(0.10);
      setShippingCost(0);
      setTaxInclusive(false);
      setRequestId(r.id || null);
      setNotes(r.notes || '');
      setOrderType(r.order_type || 'purchase');
      setSuggestByOrigIdx({});
      // Prefill order category from the request when set; buyer can still change.
      // WO-generated REQs typically have no category → buyer picks manually.
      setOrderCategoryId(r.order_category_id || '');
      setCatSuggestion(null);
      // Auto-suggest when the REQ has NO explicit category (WO-released case).
      // Fires async; if it returns a hit, we pre-fill the picker so buyer can
      // just click 保存. Silent-fail: on error we simply leave the picker empty.
      if (r.id && !r.order_category_id) {
        shizaiApi.suggestOrderCategory({ request_id: r.id })
          .then((s) => {
            if (s?.category_id && s.source !== 'none') {
              setOrderCategoryId(s.category_id);
              setCatSuggestion(s);
            }
          })
          .catch(() => {});
      }
      // Fetch per-line supplier suggestions for orphan lines. Buyer can click
      // a chip inline to slot the line into a supplier group (moves it out of
      // 「仕入先未指定」 filter automatically). See OrderLineCard's
      // ROW-0.5 chip render for the UX.
      if (r.id) {
        shizaiApi.supplierSuggestions(r.id)
          .then((data) => {
            const map = {};
            (data.suggestions_by_line || []).forEach((row) => {
              if ((row.candidates || []).length > 0) map[row.line_index] = row.candidates;
            });
            setSuggestByOrigIdx(map);
          })
          .catch(() => { /* silent — suggestion UI is optional */ });
      }
      const built = _linesFromRequest(r);
      // Only OPEN lines are eligible for a new PO (skip lines already ordered
      // by a previous PO in the multi-supplier partial-order flow).
      const openLines = built.filter((ln) => ln.line_status !== 'ordered');
      setReqAllLines(openLines);
      // Auto-pick supplier when all OPEN lines share one; otherwise force manual pick.
      const openSuppliers = Array.from(new Set(openLines.map((ln) => ln.supplier_id).filter(Boolean)));
      if (openSuppliers.length === 1) {
        setSupplierId(openSuppliers[0]);
        setSupFilter(openSuppliers[0]);
        setLines(openLines.filter((ln) => ln.supplier_id === openSuppliers[0]));
      } else if (openSuppliers.length === 0) {
        // No supplier data — show everything (legacy single-supplier request).
        setSupplierId('');
        setSupFilter('__all__');
        setLines(openLines.length ? openLines : [emptyLine()]);
      } else {
        // Multi-supplier: force buyer to pick one via the filter chip row.
        setSupplierId('');
        setSupFilter('__all__');
        setLines(openLines);
      }
    } else {
      setSupplierId(''); setOrderCategoryId(''); setCatSuggestion(null); setOrderDate(today()); setDueDate(''); setDeliveryLocation('');
      setTaxRate(0.10); setShippingCost(0); setTaxInclusive(false); setRequestId(null); setNotes(''); setOrderType('purchase');
      setReqAllLines([]); setSupFilter('__all__');
      setSuggestByOrigIdx({});
      setLines([emptyLine()]);
    }
  }, [open, order, prefillRequest]);

  // When the buyer picks a supplier filter from a multi-supplier request,
  // narrow the visible/editable line list to only that supplier's items and
  // auto-fill the top supplier field so the PO is created against that vendor.
  const applySupFilter = (val) => {
    setSupFilter(val);
    if (val === '__all__') {
      setLines(reqAllLines);
      return;
    }
    if (val === NO_SUP) {
      setLines(reqAllLines.filter((ln) => !ln.supplier_id));
      return;
    }
    setSupplierId(val);
    setLines(reqAllLines.filter((ln) => ln.supplier_id === val));
  };

  /**
   * 一括割り当て: assign ALL 仕入先未指定 lines to the currently selected
   * 仕入先 (header select) in one click. Persists each line to the REQ and
   * switches the view to that supplier so the merged PO is ready to save.
   */
  const assignNoSupToSelected = () => {
    if (!supplierId) {
      toast.error('先に仕入先を選択してください');
      return;
    }
    const sname = suppliers.find((s) => s.id === supplierId)?.name || '';
    const targets = reqAllLines.filter((l) => !l.supplier_id);
    if (targets.length === 0) return;
    setReqAllLines((prev) => {
      const nextAll = prev.map((l) => (!l.supplier_id ? { ...l, supplier_id: supplierId } : l));
      const nextFilter = supFilter === NO_SUP ? supplierId : supFilter;
      if (nextFilter === '__all__') setLines(nextAll);
      else setLines(nextAll.filter((l) => l.supplier_id === nextFilter));
      if (supFilter === NO_SUP) setSupFilter(supplierId);
      return nextAll;
    });
    setSuggestByOrigIdx((prev) => {
      const next = { ...prev };
      targets.forEach((l) => { if (l.orig_idx != null) delete next[l.orig_idx]; });
      return next;
    });
    if (requestId) {
      targets.forEach((l) => {
        if (l.orig_idx != null) shizaiApi.patchLineSupplier(requestId, l.orig_idx, supplierId).catch(() => {});
      });
    }
    toast.success(`仕入先未指定の ${targets.length}明細 を「${sname}」に割り当てました`);
  };

  // Accepts either (i, patch: object) OR (i, key: string, value) — the latter
  // for legacy inline callers, the former for OrderLineCard which passes a
  // partial state patch on every input change.
  const setLine = (i, keyOrPatch, v) => setLines((p) => p.map((l, idx) => {
    if (idx !== i) return l;
    if (typeof keyOrPatch === 'object' && keyOrPatch !== null) return { ...l, ...keyOrPatch };
    return { ...l, [keyOrPatch]: v };
  }));
  const addLine = () => setLines((p) => [...p, emptyLine()]);
  const removeLine = (i) => setLines((p) => (p.length === 1 ? p : p.filter((_, idx) => idx !== i)));

  const supplierOptions = orderType === 'subcontract'
    ? suppliers.filter((s) => ['subcontractor', 'both'].includes(s.supplier_type))
    : suppliers;

  /**
   * Apply a supplier suggestion chip: slot the line into a supplier group
   * (updates line.supplier_id in local state + drops it from the suggestion
   * map so the chip row disappears). Also mirrors the pick into
   * `reqAllLines` so the multi-supplier filter banner counts stay accurate.
   */
  const pickSupplierForLine = (i, sid, sname) => {
    setLines((p) => p.map((l, idx) => (idx === i ? { ...l, supplier_id: sid } : l)));
    // Also update the shadow array driving the filter banner
    setReqAllLines((prev) => prev.map((l) => {
      // Match by orig_idx if present (multi-line case), else by _key
      if (l.orig_idx != null && lines[i]?.orig_idx === l.orig_idx) return { ...l, supplier_id: sid };
      if (l._key && lines[i]?._key === l._key) return { ...l, supplier_id: sid };
      return l;
    }));
    // Drop this line's suggestion so the chip row disappears
    const origIdx = lines[i]?.orig_idx;
    if (origIdx != null) {
      setSuggestByOrigIdx((prev) => {
        const next = { ...prev };
        delete next[origIdx];
        return next;
      });
    }
    // If the top-level supplier picker is empty, default it to this pick
    if (!supplierId) setSupplierId(sid);
    toast.success(`#${i + 1} を「${sname}」に設定しました`);
  };

  /**
   * 仕入先の付け替え (per-line supplier picker): the buyer can MOVE any
   * request-sourced line to a different 仕入先 (or clear it). Updates the
   * dialog state, re-applies the current filter, and PERSISTS the change to
   * the parent REQ line so kanban / follow-up POs see the same assignment.
   */
  const changeLineSupplier = (i, sid) => {
    const ln = lines[i];
    if (!ln) return;
    const sname = sid ? (suppliers.find((s) => s.id === sid)?.name || '') : '未指定';
    const match = (l) => (ln.orig_idx != null ? l.orig_idx === ln.orig_idx : l._key === ln._key);
    setReqAllLines((prev) => {
      const nextAll = prev.map((l) => (match(l) ? { ...l, supplier_id: sid || null } : l));
      // Re-slice the visible lines against the current filter so a moved
      // line leaves/enters the view immediately and chip counts stay true.
      if (supFilter === '__all__') setLines(nextAll);
      else if (supFilter === NO_SUP) setLines(nextAll.filter((l) => !l.supplier_id));
      else setLines(nextAll.filter((l) => l.supplier_id === supFilter));
      return nextAll;
    });
    if (ln.orig_idx != null) {
      setSuggestByOrigIdx((prev) => {
        const next = { ...prev };
        delete next[ln.orig_idx];
        return next;
      });
    }
    if (requestId && ln.orig_idx != null) {
      shizaiApi.patchLineSupplier(requestId, ln.orig_idx, sid || null).catch(() => {});
    }
    toast.success(`#${i + 1} の仕入先を「${sname}」に変更しました`);
  };

  const applySuggestion = (i, s) => {
    // Called from OrderLineCard when the buyer clicks a 履歴から提案 chip.
    // Also pushes the historical supplier to the top-level supplier picker so
    // the buyer doesn't have to reselect it manually.
    if (s.supplier_id) setSupplierId(s.supplier_id);
    setLines((p) => p.map((l, idx) => (idx === i ? {
      ...l,
      unit_price: (Number(l.unit_price) || 0) ? l.unit_price : (s.unit_price ?? l.unit_price),
      name: l.name || s.name || '',
      part_no: l.part_no || s.part_no || '',
      drawing_no: l.drawing_no || s.drawing_no || '',
      maker: l.maker || s.maker || '',
      maker_pn: l.maker_pn || s.maker_pn || '',
      model_no: l.model_no || s.model_no || '',
      material: l.material || s.material || '',
      item_id: l.item_id || s.item_id || '',
      seibang: l.seibang || s.seibang || '',
      unit_no: l.unit_no || s.unit_no || '',
      uom: l.uom || s.uom || '個',
    } : l)));
    toast.success(`${s.supplier_name || ''} ¥${Number(s.unit_price || 0).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })} 過去データを反映`);
  };

  // 税込み入力の換算: 保存される単価と表示金額が一致するよう、単価単位で丸める
  const taxFactor = 1 + (Number(taxRate) || 0);
  const effUnitPrice = (p) => (taxInclusive ? Math.round((Number(p) || 0) / taxFactor) : (Number(p) || 0));
  const subtotal = lines.reduce((s, l) => s + Math.round((Number(l.qty) || 0) * effUnitPrice(l.unit_price)), 0);
  const shipping = Math.round(Number(shippingCost) || 0);
  const tax = Math.round((subtotal + shipping) * (Number(taxRate) || 0));
  const total = subtotal + shipping + tax;

  const save = async () => {
    // Multi-supplier guard: if source request has >1 supplier across open
    // lines, force the buyer to pick ONE via the filter chip row (so only
    // that supplier's lines flow into this PO). This preserves the invariant
    // "1 PO = 1 supplier" while still allowing the parent REQ to span many.
    if (requestId && reqAllLines.length > 0) {
      const openSups = Array.from(new Set(reqAllLines.map((ln) => ln.supplier_id).filter(Boolean)));
      const hasNoSup = reqAllLines.some((ln) => !ln.supplier_id);
      const multi = openSups.length + (hasNoSup ? 1 : 0) > 1;
      if (multi && supFilter === '__all__') {
        toast.error('複数の仕入先が含まれています。1社を選択してください。');
        return;
      }
    }
    const cleanLines = lines
      .map(({
        _key, orig_idx, supplier_id: _sid, line_status: _ls,
        was_tax_inclusive: _wti,
        ...l
      }) => ({
        ...l,
        qty: Number(l.qty) || 0,
        unit_price: effUnitPrice(l.unit_price),
        item_id: l.item_id || null,
        // Preserve requester-supplied references on the PO line so the buyer
        // still sees the clickable URL / photo / notes when the PO is later
        // reopened for review or edit. Strip empty strings → null so the DB
        // stays tidy.
        reference_url: l.reference_url || null,
        photo_url: l.photo_url || null,
        request_line_notes: l.request_line_notes || null,
      }))
      .filter((l) => (l.name || l.part_no) && l.qty > 0);
    if (cleanLines.length === 0) { toast.error(t('shizai.order.lines')); return; }
    // Collect orig_idx of every surviving line so the backend flips ONLY those
    // request lines to `ordered`. If ALL request lines are in the PO the request
    // becomes fully `ordered`; otherwise it stays `open` for the remaining lines.
    const idxs = lines
      .filter((l) => (l.name || l.part_no) && Number(l.qty) > 0)
      .map((l) => l.orig_idx)
      .filter((i) => i !== null && i !== undefined);
    setBusy(true);
    try {
      const body = {
        supplier_id: supplierId || null, request_id: requestId,
        request_line_indexes: (requestId && idxs.length) ? idxs : null,
        order_category_id: orderCategoryId || null,
        order_date: orderDate, due_date: dueDate || null,
        delivery_location: deliveryLocation || null, tax_rate: Number(taxRate) || 0,
        shipping_cost: shipping,
        order_type: orderType, lines: cleanLines, notes: notes || null,
      };
      const res = order ? await shizaiApi.updateOrder(order.id, body) : await shizaiApi.createOrder(body);
      toast.success(t('shizai.saved')); onSaved?.(res);
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBusy(false); }
  };

  /**
   * Batch-split: fan out one draft PO per supplier group on the parent
   * REQ in a single sweep. Called from the multi-supplier banner (only
   * visible when ≥2 unique open-line suppliers). No dialog reopening
   * needed — the buyer keeps drilling / confirming in one click.
   *
   * The confirmation lists every supplier + line count so the buyer sees
   * the blast radius before triggering N POs. Orphan (no-supplier) lines
   * are surfaced separately since they'll be left open on the REQ.
   */
  const handleBatchSplit = async () => {
    if (!requestId) return;
    const openSups = Array.from(new Set(reqAllLines.map((ln) => ln.supplier_id).filter(Boolean)));
    const orphanCount = reqAllLines.filter((ln) => !ln.supplier_id).length;
    const summary = openSups.map((sid) => {
      const cnt = reqAllLines.filter((ln) => ln.supplier_id === sid).length;
      const supName = suppliers.find((s) => s.id === sid)?.name || sid.slice(0, 8);
      return `• ${supName} × ${cnt}明細`;
    }).join('\n');
    const orphanTail = orphanCount > 0
      ? `\n\n仕入先未指定の ${orphanCount}明細は依頼に残ります（後で個別発注してください）。`
      : '';
    if (!window.confirm(
      `${openSups.length}件の下書き発注を作成します:\n\n${summary}${orphanTail}\n\n実行しますか？`,
    )) return;
    setBusy(true);
    try {
      const res = await shizaiApi.splitOrders(requestId, {
        tax_rate: Number(taxRate) || 10,
        order_date: orderDate || null,
        due_date: dueDate || null,
      });
      toast.success(`${res.created_orders.length}件の下書き発注を作成しました`);
      onSaved?.({ split: true, ...res });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一括発注に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" aria-describedby={undefined} data-testid="order-form-dialog">
        <SheetHeader><SheetTitle>{order ? t('shizai.order.edit') : t('shizai.order.new')}</SheetTitle></SheetHeader>

        <div className="space-y-4 mt-5">
          {/* Multi-supplier REQ chip row — appears only when the source
              request has >1 distinct supplier across its open lines. Buyer
              MUST pick one supplier so this PO covers just their items. The
              remaining lines are left `open` for a follow-up PO. */}
          {requestId && reqAllLines.length > 0 && (() => {
            const openSups = Array.from(new Set(reqAllLines.map((ln) => ln.supplier_id).filter(Boolean)));
            const hasNoSup = reqAllLines.some((ln) => !ln.supplier_id);
            const multi = openSups.length + (hasNoSup ? 1 : 0) > 1;
            if (!multi) return null;
            const canBatch = openSups.length >= 2;
            const batchLineCount = reqAllLines.filter((ln) => ln.supplier_id).length;
            return (
              <div className="rounded-lg border-2 border-amber-200 bg-amber-50/60 p-3" data-testid="order-form-sup-filter-row">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-amber-900">
                    複数の仕入先にまたがる依頼です — 1社を選んでください
                  </span>
                  <span className="text-[10px] text-amber-700">
                    ({reqAllLines.length}明細 / {openSups.length + (hasNoSup ? 1 : 0)}社)
                  </span>
                  {canBatch && (
                    <button
                      type="button"
                      data-testid="order-form-batch-split-btn"
                      onClick={handleBatchSplit}
                      disabled={busy}
                      className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-700 text-white text-xs font-semibold shadow-sm hover:from-emerald-600 hover:to-teal-700 transition-colors disabled:opacity-50"
                      title={`${openSups.length}社 × ${batchLineCount}明細をまとめて${openSups.length}件の下書き発注に分割作成します`}
                    >
                      <Lightning size={12} weight="fill" />
                      一括発注 ({openSups.length}社)
                    </button>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  <button type="button"
                    data-testid="order-form-sup-filter-all"
                    onClick={() => applySupFilter('__all__')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      supFilter === '__all__' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'
                    }`}>
                    すべて表示 ({reqAllLines.length})
                  </button>
                  {openSups.map((sid) => {
                    const cnt = reqAllLines.filter((ln) => ln.supplier_id === sid).length;
                    const supName = suppliers.find((s) => s.id === sid)?.name || sid;
                    return (
                      <button key={sid} type="button"
                        data-testid={`order-form-sup-filter-${sid}`}
                        onClick={() => applySupFilter(sid)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          supFilter === sid ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50'
                        }`}>
                        {supName} ({cnt})
                      </button>
                    );
                  })}
                  {hasNoSup && (
                    <button type="button"
                      data-testid="order-form-sup-filter-nosup"
                      onClick={() => applySupFilter(NO_SUP)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        supFilter === NO_SUP ? 'bg-zinc-600 text-white border-zinc-600' : 'bg-white text-zinc-500 border-zinc-300 hover:bg-zinc-100'
                      }`}>
                      仕入先未指定 ({reqAllLines.filter((ln) => !ln.supplier_id).length})
                    </button>
                  )}
                  {hasNoSup && supplierId && (
                    <button type="button"
                      data-testid="order-form-assign-nosup-btn"
                      onClick={assignNoSupToSelected}
                      disabled={busy}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                      title="仕入先未指定の明細をすべて、上で選択中の仕入先に割り当てます（依頼にも保存されます）">
                      <Lightning size={11} weight="fill" />
                      未指定を「{suppliers.find((s) => s.id === supplierId)?.name || ''}」に割り当て
                    </button>
                  )}
                </div>
                <p className="mt-2 text-[10px] text-amber-800">
                  {supFilter === '__all__'
                    ? '※ この状態では発注できません。1社を選択すると、その仕入先の明細だけを1件の発注にまとめられます。'
                    : `※ 選択中の仕入先の ${lines.length}明細 を1件の発注にまとめます。他の仕入先の明細は依頼に残り、後で別発注として作成できます。`}
                </p>
              </div>
            );
          })()}

          {/* Order type toggle */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="order-type-toggle">
            <Label className="text-xs font-semibold text-zinc-600">{t('shizai.order.orderType')}</Label>
            <div className="flex gap-1.5">
              {['purchase', 'subcontract'].map((ot) => (
                <button key={ot} type="button" onClick={() => setOrderType(ot)} data-testid={`order-type-opt-${ot}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${orderType === ot ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
                  {t(`shizai.orderType.${ot}`)}
                </button>
              ))}
            </div>
            {orderType === 'subcontract' && <span className="text-[11px] text-orange-600">{t('shizai.order.subcontractHint')}</span>}
          </div>
          {/* Header fields */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2"><Label className="text-xs font-semibold text-zinc-600">{orderType === 'subcontract' ? t('shizai.order.subcontractor') : t('shizai.order.supplier')}</Label>
              <Select value={supplierId || NONE} onValueChange={(v) => setSupplierId(v === NONE ? '' : v)}>
                <SelectTrigger data-testid="order-supplier-select" className="h-10 mt-1"><SelectValue placeholder={t('shizai.none')} /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>{t('shizai.none')}</SelectItem>{supplierOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="text-xs font-semibold text-zinc-600">
              受注分類 {!orderCategoryId && !order && <span className="text-rose-500">*</span>}
            </Label>
              <Select value={orderCategoryId || NONE} onValueChange={(v) => { setOrderCategoryId(v === NONE ? '' : v); setCatSuggestion(null); }}>
                <SelectTrigger data-testid="order-category-select" className="h-10 mt-1"><SelectValue placeholder="分類を選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('shizai.none')}</SelectItem>
                  {(() => {
                    // Categories are a business dimension (通常受注 / 社内受注),
                    // NOT the PO's purchase/subcontract type — never filter by
                    // orderType here (that bug emptied the whole list). Group
                    // by the master's order_type for scannability.
                    const active = orderCategories.filter((c) => c.active !== false);
                    const order = [];
                    const byGroup = new Map();
                    active.forEach((c) => {
                      const g = c.order_type || 'その他';
                      if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
                      byGroup.get(g).push(c);
                    });
                    return order.map((g) => (
                      <SelectGroup key={g}>
                        <SelectLabel className="text-[10px] text-zinc-400">{g}</SelectLabel>
                        {byGroup.get(g).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.code ? `${c.code} · ` : ''}{c.name}{c.symbol ? ` (${c.symbol})` : ''}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ));
                  })()}
                </SelectContent>
              </Select>
              {catSuggestion && orderCategoryId === catSuggestion.category_id && (
                <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"
                     data-testid="order-category-suggestion-chip"
                     title="過去の発注実績から自動で選択されました">
                  <span className="font-semibold">自動候補</span>
                  <span>·</span>
                  <span>
                    {catSuggestion.source === 'department_history_90d' && `部門実績 ${catSuggestion.match_count}/${catSuggestion.total_recent_pos}件 (90日)`}
                    {catSuggestion.source === 'type_history_90d' && `発注区分実績 ${catSuggestion.match_count}/${catSuggestion.total_recent_pos}件 (90日)`}
                    {catSuggestion.source === 'department_history_all' && `部門実績 ${catSuggestion.match_count}件`}
                    {catSuggestion.source === 'request' && '依頼元より'}
                  </span>
                </div>
              )}</div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('shizai.order.orderDate')}</Label>
              <DateField testId="order-date-input" value={orderDate} onChange={(v) => setOrderDate(v)} className="mt-1" inputClassName="h-10" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('shizai.order.dueDate')}</Label>
              <DateField testId="order-due-input" value={dueDate} onChange={(v) => setDueDate(v)} className="mt-1" inputClassName="h-10" /></div>
            <div className="col-span-2"><Label className="text-xs font-semibold text-zinc-600">{t('shizai.order.deliveryLocation')}</Label>
              <Input value={deliveryLocation} onChange={(e) => setDeliveryLocation(e.target.value)} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('shizai.order.taxRate')}</Label>
              <Input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">送料 (税抜)</Label>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-zinc-400 text-sm">¥</span>
                <Input type="number" min="0" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)}
                  className="h-10 text-right tabular-nums" data-testid="order-shipping-input" />
              </div></div>
          </div>

          {/* 税込み入力トグル — Amazon等の税込価格をそのまま入力できる */}
          <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2.5" data-testid="order-tax-inclusive-row">
            <div>
              <Label className="text-sm font-semibold text-zinc-700">単価は税込み入力</Label>
              <p className="text-xs text-zinc-500 mt-0.5">
                {taxInclusive
                  ? `入力した単価を税込みとみなし、保存時に税抜へ自動換算します（÷${taxFactor.toFixed(2)}）`
                  : 'オフ: 単価は税抜きとして扱います'}
              </p>
            </div>
            <Switch data-testid="order-tax-inclusive-toggle" checked={taxInclusive} onCheckedChange={setTaxInclusive} />
          </div>

          {/* Lines */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="text-sm font-semibold text-zinc-700">{t('shizai.order.lines')}</Label>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="order-add-line-btn" className="h-8" disabled={busy}><Plus size={15} className="mr-1" /> {t('shizai.order.addLine')}</Button>
          </div>

          <div className="space-y-3">
            {lines.map((l, i) => (
              <OrderLineCard
                key={l._key || i}
                line={l}
                index={i}
                items={items}
                setLine={setLine}
                onRemove={() => removeLine(i)}
                canRemove={lines.length > 1}
                onPickHistory={applySuggestion}
                supplierCandidates={l.orig_idx != null ? suggestByOrigIdx[l.orig_idx] : undefined}
                onPickSupplier={pickSupplierForLine}
                suppliers={requestId && l.orig_idx != null ? suppliers : undefined}
                onChangeSupplier={changeLineSupplier}
                taxDivisor={taxInclusive ? taxFactor : 1}
                disableEdit={busy}
              />
            ))}
          </div>

          {/* Totals */}
          <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 ml-auto max-w-xs text-sm" data-testid="order-totals">
            <div className="flex justify-between py-0.5"><span className="text-zinc-500">{t('shizai.order.subtotal')}{taxInclusive && <span className="ml-1 text-[10px] text-orange-600">(税抜換算後)</span>}</span><span className="tabular-nums">{yen(subtotal)}</span></div>
            {shipping > 0 && (
              <div className="flex justify-between py-0.5" data-testid="order-shipping-row"><span className="text-zinc-500">送料</span><span className="tabular-nums">{yen(shipping)}</span></div>
            )}
            <div className="flex justify-between py-0.5"><span className="text-zinc-500">{t('shizai.order.tax')}</span><span className="tabular-nums">{yen(tax)}</span></div>
            <div className="flex justify-between py-0.5 border-t border-zinc-200 mt-1 pt-1.5 font-bold text-zinc-900"><span>{t('shizai.order.total')}</span><span className="tabular-nums" data-testid="order-total-value">{yen(total)}</span></div>
          </div>

          <div><Label className="text-xs font-semibold text-zinc-600">{t('shizai.order.notes')}</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-10 mt-1" /></div>
        </div>

        <SheetFooter className="mt-6 flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('shizai.cancel')}</Button>
          <Button data-testid="order-save-btn" onClick={save} disabled={busy}>{busy ? t('shizai.saving') : t('shizai.save')}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
